import { type AuthLocale, getAuthLocale } from './authI18n';
import type { LangFn } from './localization';
import type { RegularLangFnParameters } from './localization';

type ErrorTranslator = {
  known: Record<string, string>;
  phoneNumberFallback: string;
  phoneCodeFallback: string;
  floodFallback: string;
  passwordTooFreshFallback: string;
  passwordFallback: string;
  sessionRevokedFallback: string;
  authRestartFallback: string;
  srpInvalidFallback: string;
};

const AUTH_ERROR_MESSAGES: Record<AuthLocale, ErrorTranslator> = {
  'zh-CN': {
    known: {
      PHONE_NUMBER_INVALID: '手机号格式不正确，请检查后重试',
      PHONE_NUMBER_BANNED: '该手机号暂时无法使用，请更换号码后重试',
      PHONE_NUMBER_FLOOD: '请求过于频繁，请稍后再试',
      PHONE_CODE_EMPTY: '请输入验证码',
      PHONE_CODE_INVALID: '验证码错误，请重新输入',
      PHONE_CODE_EXPIRED: '验证码已过期，请重新获取',
      SESSION_PASSWORD_NEEDED: '该账号已开启二级密码，请继续输入二级密码',
      PASSWORD_HASH_INVALID: '二级密码错误，请重新输入',
    },
    phoneNumberFallback: '手机号校验失败，请检查后重试',
    phoneCodeFallback: '验证码校验失败，请重新输入',
    floodFallback: '操作过于频繁，请稍后再试',
    passwordTooFreshFallback: '二级密码刚修改不久，请稍后再试',
    passwordFallback: '二级密码错误，请重新输入',
    sessionRevokedFallback: '当前登录会话已失效，请重新验证',
    authRestartFallback: '验证流程已重置，请重新开始',
    srpInvalidFallback: '二级密码校验失败，请重新输入',
  },
  'zh-TW': {
    known: {
      PHONE_NUMBER_INVALID: '手機號碼格式不正確，請檢查後再試',
      PHONE_NUMBER_BANNED: '此手機號碼暫時無法使用，請更換號碼後再試',
      PHONE_NUMBER_FLOOD: '請求過於頻繁，請稍後再試',
      PHONE_CODE_EMPTY: '請輸入驗證碼',
      PHONE_CODE_INVALID: '驗證碼錯誤，請重新輸入',
      PHONE_CODE_EXPIRED: '驗證碼已過期，請重新獲取',
      SESSION_PASSWORD_NEEDED: '此帳號已開啟兩步驗證，請繼續輸入兩步密碼',
      PASSWORD_HASH_INVALID: '兩步密碼錯誤，請重新輸入',
    },
    phoneNumberFallback: '手機號碼驗證失敗，請檢查後重試',
    phoneCodeFallback: '驗證碼驗證失敗，請重新輸入',
    floodFallback: '操作過於頻繁，請稍後再試',
    passwordTooFreshFallback: '兩步密碼剛修改不久，請稍後再試',
    passwordFallback: '兩步密碼錯誤，請重新輸入',
    sessionRevokedFallback: '目前登入工作階段已失效，請重新驗證',
    authRestartFallback: '驗證流程已重置，請重新開始',
    srpInvalidFallback: '兩步密碼驗證失敗，請重新輸入',
  },
  en: {
    known: {
      PHONE_NUMBER_INVALID: 'The phone number format is invalid. Please check and try again.',
      PHONE_NUMBER_BANNED: 'This phone number cannot be used right now. Please try another number.',
      PHONE_NUMBER_FLOOD: 'Too many requests. Please try again later.',
      PHONE_CODE_EMPTY: 'Please enter the verification code.',
      PHONE_CODE_INVALID: 'The verification code is incorrect. Please try again.',
      PHONE_CODE_EXPIRED: 'The verification code has expired. Please request a new one.',
      SESSION_PASSWORD_NEEDED: 'Two-step verification is enabled on this account. Please enter your password.',
      PASSWORD_HASH_INVALID: 'The two-step password is incorrect. Please try again.',
    },
    phoneNumberFallback: 'Phone number verification failed. Please check and try again.',
    phoneCodeFallback: 'Verification code validation failed. Please enter it again.',
    floodFallback: 'Too many attempts. Please try again later.',
    passwordTooFreshFallback: 'The two-step password was changed recently. Please try again later.',
    passwordFallback: 'The two-step password is incorrect. Please try again.',
    sessionRevokedFallback: 'The current login session is no longer valid. Please verify again.',
    authRestartFallback: 'The verification flow has been reset. Please start again.',
    srpInvalidFallback: 'Two-step password verification failed. Please try again.',
  },
  de: {
    known: {
      PHONE_NUMBER_INVALID: 'Das Telefonnummernformat ist ungueltig. Bitte pruefen Sie es und versuchen Sie es erneut.',
      PHONE_NUMBER_BANNED: 'Diese Telefonnummer kann derzeit nicht verwendet werden. Bitte nutzen Sie eine andere Nummer.',
      PHONE_NUMBER_FLOOD: 'Zu viele Anfragen. Bitte versuchen Sie es spaeter erneut.',
      PHONE_CODE_EMPTY: 'Bitte geben Sie den Bestätigungscode ein.',
      PHONE_CODE_INVALID: 'Der Bestätigungscode ist falsch. Bitte versuchen Sie es erneut.',
      PHONE_CODE_EXPIRED: 'Der Bestätigungscode ist abgelaufen. Bitte fordern Sie einen neuen an.',
      SESSION_PASSWORD_NEEDED: 'Fuer dieses Konto ist die Zwei-Faktor-Verifizierung aktiviert. Bitte geben Sie Ihr Passwort ein.',
      PASSWORD_HASH_INVALID: 'Das Zwei-Faktor-Passwort ist falsch. Bitte versuchen Sie es erneut.',
    },
    phoneNumberFallback: 'Die Telefonnummernpruefung ist fehlgeschlagen. Bitte pruefen Sie die Nummer und versuchen Sie es erneut.',
    phoneCodeFallback: 'Die Pruefung des Bestätigungscodes ist fehlgeschlagen. Bitte geben Sie ihn erneut ein.',
    floodFallback: 'Zu viele Versuche. Bitte versuchen Sie es spaeter erneut.',
    passwordTooFreshFallback: 'Das Zwei-Faktor-Passwort wurde kuerzlich geaendert. Bitte versuchen Sie es spaeter erneut.',
    passwordFallback: 'Das Zwei-Faktor-Passwort ist falsch. Bitte versuchen Sie es erneut.',
    sessionRevokedFallback: 'Die aktuelle Sitzung ist ungueltig. Bitte fuehren Sie die Verifizierung erneut durch.',
    authRestartFallback: 'Der Verifizierungsablauf wurde zurueckgesetzt. Bitte starten Sie erneut.',
    srpInvalidFallback: 'Die Zwei-Faktor-Verifizierung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
  },
  fr: {
    known: {
      PHONE_NUMBER_INVALID: 'Le format du numero de telephone est invalide. Veuillez verifier puis reessayer.',
      PHONE_NUMBER_BANNED: 'Ce numero de telephone ne peut pas etre utilise pour le moment. Veuillez en essayer un autre.',
      PHONE_NUMBER_FLOOD: 'Trop de requetes. Veuillez reessayer plus tard.',
      PHONE_CODE_EMPTY: 'Veuillez saisir le code de verification.',
      PHONE_CODE_INVALID: 'Le code de verification est incorrect. Veuillez reessayer.',
      PHONE_CODE_EXPIRED: 'Le code de verification a expire. Veuillez en demander un nouveau.',
      SESSION_PASSWORD_NEEDED: 'La verification en deux etapes est activee pour ce compte. Veuillez saisir votre mot de passe.',
      PASSWORD_HASH_INVALID: 'Le mot de passe en deux etapes est incorrect. Veuillez reessayer.',
    },
    phoneNumberFallback: 'La verification du numero de telephone a echoue. Veuillez verifier puis reessayer.',
    phoneCodeFallback: 'La verification du code a echoue. Veuillez le saisir a nouveau.',
    floodFallback: 'Trop de tentatives. Veuillez reessayer plus tard.',
    passwordTooFreshFallback: 'Le mot de passe en deux etapes a ete modifie recemment. Veuillez reessayer plus tard.',
    passwordFallback: 'Le mot de passe en deux etapes est incorrect. Veuillez reessayer.',
    sessionRevokedFallback: 'La session actuelle n’est plus valide. Veuillez relancer la verification.',
    authRestartFallback: 'Le processus de verification a ete reinitialise. Veuillez recommencer.',
    srpInvalidFallback: 'La verification du mot de passe en deux etapes a echoue. Veuillez reessayer.',
  },
  it: {
    known: {
      PHONE_NUMBER_INVALID: 'Il formato del numero di telefono non e valido. Controllalo e riprova.',
      PHONE_NUMBER_BANNED: 'Questo numero di telefono non puo essere usato al momento. Prova con un altro numero.',
      PHONE_NUMBER_FLOOD: 'Troppe richieste. Riprova piu tardi.',
      PHONE_CODE_EMPTY: 'Inserisci il codice di verifica.',
      PHONE_CODE_INVALID: 'Il codice di verifica non e corretto. Riprova.',
      PHONE_CODE_EXPIRED: 'Il codice di verifica e scaduto. Richiedine uno nuovo.',
      SESSION_PASSWORD_NEEDED: 'La verifica in due passaggi e attiva per questo account. Inserisci la password.',
      PASSWORD_HASH_INVALID: 'La password in due passaggi non e corretta. Riprova.',
    },
    phoneNumberFallback: 'La verifica del numero di telefono non e riuscita. Controllalo e riprova.',
    phoneCodeFallback: 'La verifica del codice non e riuscita. Inseriscilo di nuovo.',
    floodFallback: 'Troppi tentativi. Riprova piu tardi.',
    passwordTooFreshFallback: 'La password in due passaggi e stata cambiata di recente. Riprova piu tardi.',
    passwordFallback: 'La password in due passaggi non e corretta. Riprova.',
    sessionRevokedFallback: 'La sessione corrente non e piu valida. Esegui di nuovo la verifica.',
    authRestartFallback: 'Il flusso di verifica e stato reimpostato. Ricomincia.',
    srpInvalidFallback: 'La verifica della password in due passaggi non e riuscita. Riprova.',
  },
  es: {
    known: {
      PHONE_NUMBER_INVALID: 'El formato del numero de telefono no es valido. Revisalo e intentalo de nuevo.',
      PHONE_NUMBER_BANNED: 'Este numero de telefono no se puede usar ahora mismo. Prueba con otro numero.',
      PHONE_NUMBER_FLOOD: 'Demasiadas solicitudes. Intentalo de nuevo mas tarde.',
      PHONE_CODE_EMPTY: 'Introduce el codigo de verificacion.',
      PHONE_CODE_INVALID: 'El codigo de verificacion es incorrecto. Intentalo de nuevo.',
      PHONE_CODE_EXPIRED: 'El codigo de verificacion ha caducado. Solicita uno nuevo.',
      SESSION_PASSWORD_NEEDED: 'La verificacion en dos pasos esta activada para esta cuenta. Introduce la contrasena.',
      PASSWORD_HASH_INVALID: 'La contrasena en dos pasos es incorrecta. Intentalo de nuevo.',
    },
    phoneNumberFallback: 'La verificacion del numero de telefono ha fallado. Revisalo e intentalo de nuevo.',
    phoneCodeFallback: 'La verificacion del codigo ha fallado. Introducelo otra vez.',
    floodFallback: 'Demasiados intentos. Intentalo de nuevo mas tarde.',
    passwordTooFreshFallback: 'La contrasena en dos pasos se cambio recientemente. Intentalo de nuevo mas tarde.',
    passwordFallback: 'La contrasena en dos pasos es incorrecta. Intentalo de nuevo.',
    sessionRevokedFallback: 'La sesion actual ya no es valida. Vuelve a verificarla.',
    authRestartFallback: 'El proceso de verificacion se ha reiniciado. Empieza de nuevo.',
    srpInvalidFallback: 'La verificacion de la contrasena en dos pasos ha fallado. Intentalo de nuevo.',
  },
  pt: {
    known: {
      PHONE_NUMBER_INVALID: 'O formato do numero de telefone e invalido. Verifique e tente novamente.',
      PHONE_NUMBER_BANNED: 'Este numero de telefone nao pode ser usado no momento. Tente outro numero.',
      PHONE_NUMBER_FLOOD: 'Muitas solicitacoes. Tente novamente mais tarde.',
      PHONE_CODE_EMPTY: 'Digite o codigo de verificacao.',
      PHONE_CODE_INVALID: 'O codigo de verificacao esta incorreto. Tente novamente.',
      PHONE_CODE_EXPIRED: 'O codigo de verificacao expirou. Solicite um novo.',
      SESSION_PASSWORD_NEEDED: 'A verificacao em duas etapas esta ativada nesta conta. Digite sua senha.',
      PASSWORD_HASH_INVALID: 'A senha em duas etapas esta incorreta. Tente novamente.',
    },
    phoneNumberFallback: 'A verificacao do numero de telefone falhou. Verifique e tente novamente.',
    phoneCodeFallback: 'A verificacao do codigo falhou. Digite-o novamente.',
    floodFallback: 'Muitas tentativas. Tente novamente mais tarde.',
    passwordTooFreshFallback: 'A senha em duas etapas foi alterada recentemente. Tente novamente mais tarde.',
    passwordFallback: 'A senha em duas etapas esta incorreta. Tente novamente.',
    sessionRevokedFallback: 'A sessao atual nao e mais valida. Faca a verificacao novamente.',
    authRestartFallback: 'O fluxo de verificacao foi reiniciado. Comece de novo.',
    srpInvalidFallback: 'A verificacao da senha em duas etapas falhou. Tente novamente.',
  },
  ja: {
    known: {
      PHONE_NUMBER_INVALID: '電話番号の形式が正しくありません。確認してもう一度お試しください。',
      PHONE_NUMBER_BANNED: 'この電話番号は現在使用できません。別の番号をお試しください。',
      PHONE_NUMBER_FLOOD: 'リクエストが多すぎます。しばらくしてからもう一度お試しください。',
      PHONE_CODE_EMPTY: '認証コードを入力してください。',
      PHONE_CODE_INVALID: '認証コードが正しくありません。もう一度入力してください。',
      PHONE_CODE_EXPIRED: '認証コードの有効期限が切れています。再取得してください。',
      SESSION_PASSWORD_NEEDED: 'このアカウントでは二段階認証が有効です。パスワードを入力してください。',
      PASSWORD_HASH_INVALID: '二段階認証パスワードが正しくありません。もう一度お試しください。',
    },
    phoneNumberFallback: '電話番号の確認に失敗しました。確認してもう一度お試しください。',
    phoneCodeFallback: '認証コードの確認に失敗しました。もう一度入力してください。',
    floodFallback: '操作が多すぎます。しばらくしてからもう一度お試しください。',
    passwordTooFreshFallback: '二段階認証パスワードが最近変更されました。しばらくしてからもう一度お試しください。',
    passwordFallback: '二段階認証パスワードが正しくありません。もう一度お試しください。',
    sessionRevokedFallback: '現在のログインセッションは無効です。再度認証してください。',
    authRestartFallback: '認証フローがリセットされました。もう一度最初からやり直してください。',
    srpInvalidFallback: '二段階認証パスワードの確認に失敗しました。もう一度お試しください。',
  },
  ko: {
    known: {
      PHONE_NUMBER_INVALID: '전화번호 형식이 올바르지 않습니다. 확인 후 다시 시도하세요.',
      PHONE_NUMBER_BANNED: '이 전화번호는 현재 사용할 수 없습니다. 다른 번호를 시도하세요.',
      PHONE_NUMBER_FLOOD: '요청이 너무 많습니다. 잠시 후 다시 시도하세요.',
      PHONE_CODE_EMPTY: '인증 코드를 입력하세요.',
      PHONE_CODE_INVALID: '인증 코드가 올바르지 않습니다. 다시 입력하세요.',
      PHONE_CODE_EXPIRED: '인증 코드가 만료되었습니다. 새 코드를 요청하세요.',
      SESSION_PASSWORD_NEEDED: '이 계정에는 2단계 인증이 설정되어 있습니다. 비밀번호를 입력하세요.',
      PASSWORD_HASH_INVALID: '2단계 비밀번호가 올바르지 않습니다. 다시 시도하세요.',
    },
    phoneNumberFallback: '전화번호 확인에 실패했습니다. 확인 후 다시 시도하세요.',
    phoneCodeFallback: '인증 코드 확인에 실패했습니다. 다시 입력하세요.',
    floodFallback: '시도가 너무 많습니다. 잠시 후 다시 시도하세요.',
    passwordTooFreshFallback: '2단계 비밀번호가 최근 변경되었습니다. 잠시 후 다시 시도하세요.',
    passwordFallback: '2단계 비밀번호가 올바르지 않습니다. 다시 시도하세요.',
    sessionRevokedFallback: '현재 로그인 세션이 더 이상 유효하지 않습니다. 다시 인증하세요.',
    authRestartFallback: '인증 절차가 재설정되었습니다. 처음부터 다시 시작하세요.',
    srpInvalidFallback: '2단계 비밀번호 확인에 실패했습니다. 다시 시도하세요.',
  },
  th: {
    known: {
      PHONE_NUMBER_INVALID: 'รูปแบบหมายเลขโทรศัพท์ไม่ถูกต้อง โปรดตรวจสอบแล้วลองอีกครั้ง',
      PHONE_NUMBER_BANNED: 'ไม่สามารถใช้หมายเลขโทรศัพท์นี้ได้ในขณะนี้ โปรดลองหมายเลขอื่น',
      PHONE_NUMBER_FLOOD: 'มีคำขอมากเกินไป โปรดลองอีกครั้งในภายหลัง',
      PHONE_CODE_EMPTY: 'กรุณากรอกรหัสยืนยัน',
      PHONE_CODE_INVALID: 'รหัสยืนยันไม่ถูกต้อง โปรดลองอีกครั้ง',
      PHONE_CODE_EXPIRED: 'รหัสยืนยันหมดอายุแล้ว โปรดขอรหัสใหม่',
      SESSION_PASSWORD_NEEDED: 'บัญชีนี้เปิดใช้งานการยืนยันแบบสองขั้นตอน โปรดกรอกรหัสผ่านของคุณ',
      PASSWORD_HASH_INVALID: 'รหัสผ่านแบบสองขั้นตอนไม่ถูกต้อง โปรดลองอีกครั้ง',
    },
    phoneNumberFallback: 'การตรวจสอบหมายเลขโทรศัพท์ล้มเหลว โปรดตรวจสอบแล้วลองอีกครั้ง',
    phoneCodeFallback: 'การตรวจสอบรหัสยืนยันล้มเหลว โปรดกรอกใหม่อีกครั้ง',
    floodFallback: 'มีการพยายามมากเกินไป โปรดลองอีกครั้งในภายหลัง',
    passwordTooFreshFallback: 'รหัสผ่านแบบสองขั้นตอนเพิ่งถูกเปลี่ยน โปรดลองอีกครั้งภายหลัง',
    passwordFallback: 'รหัสผ่านแบบสองขั้นตอนไม่ถูกต้อง โปรดลองอีกครั้ง',
    sessionRevokedFallback: 'เซสชันการเข้าสู่ระบบปัจจุบันใช้ไม่ได้แล้ว โปรดยืนยันอีกครั้ง',
    authRestartFallback: 'ขั้นตอนการยืนยันถูกรีเซ็ตแล้ว โปรดเริ่มใหม่อีกครั้ง',
    srpInvalidFallback: 'การตรวจสอบรหัสผ่านแบบสองขั้นตอนล้มเหลว โปรดลองอีกครั้ง',
  },
};

function getAuthErrorKeyValue(errorKey?: string | RegularLangFnParameters) {
  if (!errorKey) {
    return undefined;
  }

  if (typeof errorKey === 'string') {
    return errorKey;
  }

  if (typeof errorKey === 'object' && 'key' in errorKey && typeof errorKey.key === 'string') {
    return errorKey.key;
  }

  return undefined;
}

export function getAuthErrorFallback(
  errorKey: string | RegularLangFnParameters | undefined,
  lang?: Pick<LangFn, 'withRegular'>,
) {
  if (!errorKey) {
    return undefined;
  }

  if (typeof errorKey === 'string') {
    return errorKey;
  }

  if (!lang) {
    return undefined;
  }

  try {
    return lang.withRegular(errorKey);
  } catch (err) {
    const normalizedErrorKey = getAuthErrorKeyValue(errorKey);
    return normalizedErrorKey;
  }
}

export default function localizeAuthError(
  errorKey?: string | RegularLangFnParameters,
  locale = getAuthLocale(),
  fallback?: string,
) {
  const normalizedErrorKey = getAuthErrorKeyValue(errorKey);
  if (!normalizedErrorKey) {
    return fallback;
  }

  const translator = AUTH_ERROR_MESSAGES[locale];
  if (translator.known[normalizedErrorKey]) {
    return translator.known[normalizedErrorKey];
  }

  if (normalizedErrorKey.includes('PHONE_NUMBER')) {
    return translator.phoneNumberFallback;
  }

  if (normalizedErrorKey.includes('PHONE_CODE')) {
    return translator.phoneCodeFallback;
  }

  if (normalizedErrorKey.includes('FLOOD')) {
    return translator.floodFallback;
  }

  if (normalizedErrorKey.includes('PASSWORD') && normalizedErrorKey.includes('TOO_FRESH')) {
    return translator.passwordTooFreshFallback;
  }

  if (normalizedErrorKey.includes('PASSWORD')) {
    return translator.passwordFallback;
  }

  if (normalizedErrorKey.includes('SESSION_REVOKED')) {
    return translator.sessionRevokedFallback;
  }

  if (normalizedErrorKey.includes('AUTH_RESTART')) {
    return translator.authRestartFallback;
  }

  if (normalizedErrorKey.includes('SRP_ID_INVALID')) {
    return translator.srpInvalidFallback;
  }

  return fallback;
}
